<template>
	<div class="theme-manage-form">

		<h2 class="section-title-xs">{{ subscriptionsYourDetails }}</h2>

		<theme-input 
			:label="usersCompanyName"
			:placeholder="usersCompanyNamePlaceholder"
			name="company[company_name]" 
			v-validate="`alpha_num_spaces`">
		</theme-input>

		<theme-radios 
			name="title_id"
			:options="usersSex"
			:rules="`required|make_choice`">
		</theme-radios>

		<theme-input 
			:label="usersFirstName" 
			:placeholder="usersFirstNamePlaceholder"
			name="first_name" 
			v-validate="`required|alpha_spaces_dash`"
			:isRequired="true"
		></theme-input>

		<theme-input 
			:label="usersMiddleName"
			:placeholder="usersMiddleNamePlaceholder"
			name="middle_name"
		></theme-input>

		<theme-input 
			:label="usersLastName"
			:placeholder="usersLastNamePlaceholder"
			name="last_name" 
			v-validate="`required|alpha_spaces_dash`"
			:isRequired="true"
		></theme-input>

		<theme-input 
			:label="usersPhoneNumber" 
			:placeholder="usersPhoneNumberPlaceholder"
			name="phone_number" 
			v-validate="`required|phone_number`"
			:isRequired="true"
		></theme-input>

		<div>
			<theme-input 
				:label="usersEmail" 
				:placeholder="usersEmailPlaceholder"
				name="email" 
				v-validate="`required|email`" 
				@blur="checkMail"
				:isRequired="true"
			></theme-input>

			<div class="mt-2" v-if="!!is_user_already_exists">
				<span class="text-[#FF695B] text-[12px]">{{ userAlreadyExists }}</span>
				<input type="hidden" name="user_already_exists" v-validate="`required`" />
			</div>
		</div>


		<h2 class="section-title-xs">{{ propManageDetails }}</h2>

		<theme-select
			:label="propCountry"
			:value="country_code"
			@input="onCountryChanged"
			name="country_code"
			:options="countryCodeOptions"
			v-validate="`required`"
			:isRequired="true"
		></theme-select>

		<!--
			NL: App\Http\Controllers\AddressLookupController-backed PDOK typeahead - this page
			never had ANY address lookup before (plain manual postcode/house-number/street/
			location fields only), so this is a net addition, not a change to existing NL
			behaviour. A Google-supported foreign country gets the same GoogleAddressTypeahead.vue
			used by add-house.blade.php/properties/edit.blade.php; anything else stays manual.
		-->
		<div v-if="isNLProperty">
			<theme-input
				:label="propPostCode"
				:placeholder="propPostCodePlaceholder"
				name="postcode"
				v-model.trim="postcode"
				@input="clearAddressLookupFeedback"
				v-validate="`required|post_code`"
				:isRequired="true"
			></theme-input>

			<theme-input
				:label="propBuildingUid"
				:placeholder="propBuildingUidPlaceholder"
				name="building_uid"
				v-model.trim="building_uid"
				@input="clearAddressLookupFeedback"
				v-validate="`required`"
				:isRequired="true"
			></theme-input>

			<theme-input
				:label="propBuildingUidApp"
				:placeholder="propBuildingUidAppPlaceholder"
				name="building_uid_appendix"
				v-model.trim="building_uid_appendix"
				@input="clearAddressLookupFeedback"
			></theme-input>
		</div>

		<pdok-address-typeahead
			v-if="isNLProperty"
			:postcode="postcode"
			:house-number="building_uid"
			:addition="building_uid_appendix"
			:suggest-url="pdokSuggestUrl"
			:resolve-url="pdokResolveUrl"
			:locale="locale"
			@resolved="onPdokAddressResolved"
			@unavailable="onPdokUnavailable"
		></pdok-address-typeahead>

		<google-address-typeahead
			v-else-if="isGoogleSupportedCountry"
			:country-code="country_code"
			:suggest-url="internationalAddressSuggestUrl"
			:validate-url="internationalAddressValidateUrl"
			:locale="locale"
			theme="public"
			@resolved="onInternationalAddressResolved"
		></google-address-typeahead>

		<p v-else class="text-sm mt-1" style="color: #777">
			{{ addressManualEntryNotice }}
		</p>

		<div v-if="addressLookupError" class="text-sm mt-1" style="color: red">{{ noResultsText }}</div>

		<div v-if="!isNLProperty">
			<theme-input
				:label="propPostCode"
				:placeholder="propPostCodePlaceholder"
				name="postcode"
				v-model.trim="postcode"
			></theme-input>

			<theme-input
				:label="propBuildingUid"
				:placeholder="propBuildingUidPlaceholder"
				name="building_uid"
				v-model.trim="building_uid"
				v-validate="`required`"
				:isRequired="true"
			></theme-input>
		</div>

		<theme-input
			:label="propStreet"
			:placeholder="propStreetPlaceholder"
			name="street"
			v-model="street"
			v-validate="`required`"
			:isRequired="true"
		></theme-input>

		<theme-autocomplete
			name='location_id'
			v-model='location'
			:label="propLocation"
			:placeholder="propLocationPlaceholder"
			endpoint="/admin/autocomplete/locations"
			ref="locationAutocomplete"
			:resolve="(item) => item.name"
			v-validate="`required|location`"
			:isRequired="true"
		></theme-autocomplete>

		<div v-if="!isNLProperty">
			<theme-input
				:label="propAdministrativeArea"
				name="administrative_area"
				v-model="administrative_area"
				:placeholder="propAdministrativeArea"
			></theme-input>

			<theme-input
				:label="propAddressLine2"
				name="address_line_2"
				v-model="address_line_2"
				:placeholder="propAddressLine2"
			></theme-input>
		</div>

		<!--
			Never real form fields - only ever set by onPdokAddressResolved()/
			onInternationalAddressResolved() above, so Controller::postManage() can server-side
			re-verify a claimed "verified"/"correction_requires_confirmation" status (App\Services\
			Address\AddressResolutionStore), rather than trusting it as submitted.
		-->
		<input type="hidden" name="address_source" :value="address_source">
		<input type="hidden" name="address_validation_status" :value="address_validation_status">
		<input type="hidden" name="address_provider_reference" :value="address_provider_reference">
		<input type="hidden" name="address_resolution_id" :value="address_resolution_id">

		<theme-input 
			:label="propDesiredRentalPrice" 
			type="number" 
			name="price" 
			:placeholder="'€ ' + propMonth"
		></theme-input>

		<theme-radios 
			:label="propAlreadyRent"
			name="is_rented"
			:options="propAlreadyRentOpt"
			:rules="`required|make_choice`"
			:isRequired="true"
		></theme-radios>

		<a class="theme-btn theme-btn--primary" :href="calculatePriceLink" target="_blank">
			{{ locales && locales.properties && locales.properties.calculate_property_rent_price }}
		</a>

		<div v-if="isRecaptcha === 'true'" class="form__item">
			<v-recaptcha @validate="checkRecaptcha" :site-key="recaptchaKey"></v-recaptcha>
		</div>

		<button class="theme-btn theme-btn--primary" type="submit" @click.prevent="submitForm">
			{{ buttonName }}
		</button>

	</div>
</template>

<script>
import sweetalert from "sweetalert";
import $ from "jquery";
import { mapGetters, mapActions } from "vuex";

export default {
	inject: ['$validator'],
	components: {
	},
	data () {
		return {
			is_user_already_exists: 0,
			recaptchaTicked: false,
			country_code: 'NL',
			postcode: '',
			building_uid: '',
			building_uid_appendix: '',
			street: '',
			location: null,
			administrative_area: '',
			address_line_2: '',
			address_source: '',
			address_validation_status: '',
			address_provider_reference: '',
			address_resolution_id: '',
			pdokUnavailable: false,
			addressLookupError: false,
		}
	},
	computed: {
		...mapGetters(["locales"]),
		isNLProperty() {
			return (this.country_code || 'NL').toUpperCase() === 'NL';
		},
		isGoogleSupportedCountry() {
			return !this.isNLProperty && this.googleSupportedCountries.indexOf((this.country_code || '').toUpperCase()) !== -1;
		},
	},
	props: {
		locale: {
			default: 'nl',
			type: String
		},
		countryCodeOptions: {
			default: () => {},
			type: Object
		},
		googleSupportedCountries: {
			default: () => [],
			type: Array
		},
		pdokSuggestUrl: {
			default: '',
			type: String
		},
		pdokResolveUrl: {
			default: '',
			type: String
		},
		internationalAddressSuggestUrl: {
			default: '',
			type: String
		},
		internationalAddressValidateUrl: {
			default: '',
			type: String
		},
		addressManualEntryNotice: {
			default: '',
			type: String
		},
		noResultsText: {
			default: '',
			type: String
		},
		propCountry: {
			default: '',
			type: String
		},
		propAdministrativeArea: {
			default: '',
			type: String
		},
		propAddressLine2: {
			default: '',
			type: String
		},
		buttonName: {
			default: 'Submit',
			type: String
		},
		buttonAlign: {
			default: 'left',
			type: String
		},
		showSuccess: {
			default: 1,
			type: Number
		},
		successText: {
			default: '',
			type: String
		},
		errorText: {
			default: '',
			type: String
		},
		isRecaptcha: {
			default: 'false',
			type: String
		},
		recaptchaMessage: {
			default: 'Please tick recaptcha',
			type: String
		},
		recaptchaKey: {
			default: '',
			type: String
		},
		subscriptionsYourDetails: {
			default: '',
			type: String
		},
		usersCompanyName: {
			default: '',
			type: String
		},
		usersCompanyNamePlaceholder: {
			default: '',
			type: String
		},
		usersSex: {
			default: () => {},
			type: Object
		},
		usersFirstName: {
			default: '',
			type: String
		},
		usersFirstNamePlaceholder: {
			default: '',
			type: String
		},
		usersMiddleName: {
			default: '',
			type: String
		},
		usersMiddleNamePlaceholder: {
			default: '',
			type: String
		},
		usersLastName: {
			default: '',
			type: String
		},
		usersLastNamePlaceholder: {
			default: '',
			type: String
		},
		usersPhoneNumber: {
			default: '',
			type: String
		},
		usersPhoneNumberPlaceholder: {
			default: '',
			type: String
		},
		usersEmail: {
			default: '',
			type: String
		},
		usersEmailPlaceholder: {
			default: '',
			type: String
		},
		userAlreadyExists: {
			default: '',
			type: String
		},
		propManageDetails: {
			default: '',
			type: String
		},
		propPostCode: {
			default: '',
			type: String
		},
		propPostCodePlaceholder: {
			default: '',
			type: String
		},
		propBuildingUid: {
			default: '',
			type: String
		},
		propBuildingUidPlaceholder: {
			default: '',
			type: String
		},
		propBuildingUidApp: {
			default: '',
			type: String
		},
		propBuildingUidAppPlaceholder: {
			default: '',
			type: String
		},
		propStreet: {
			default: '',
			type: String
		},
		propStreetPlaceholder: {
			default: '',
			type: String
		},
		propLocation: {
			default: '',
			type: String
		},
		propLocationPlaceholder: {
			default: '',
			type: String
		},
		propDesiredRentalPrice: {
			default: '',
			type: String
		},
		propDesiredRentalPricePlaceholder: {
			default: '',
			type: String
		},
		propMonth: {
			default: '',
			type: String
		},
		propMonthPlaceholder: {
			default: '',
			type: String
		},
		propAlreadyRent: {
			default: '',
			type: String
		},
		propAlreadyRentOpt: {
			default: () => {},
			type: Object
    },
    calculatePriceLink: {
			default: '',
			type: String
		},
	},
	methods: {
		clearAddressLookupFeedback() {
			this.addressLookupError = false;
		},
		// A country change invalidates whatever address state was mid-flight for the PREVIOUS
		// country - never carry a resolved/pending Dutch or foreign address over to a newly-
		// selected, different country. Mirrors add-house.blade.php's onCountryChanged.
		onCountryChanged(code) {
			this.country_code = code;
			this.postcode = '';
			this.building_uid = '';
			this.building_uid_appendix = '';
			this.street = '';
			this.location = null;
			this.administrative_area = '';
			this.address_line_2 = '';
			this.address_source = '';
			this.address_validation_status = '';
			this.address_provider_reference = '';
			this.address_resolution_id = '';
			this.pdokUnavailable = false;
			this.addressLookupError = false;
		},
		onPdokUnavailable() {
			this.pdokUnavailable = true;
		},
		onPdokAddressResolved(payload) {
			this.addressLookupError = false;
			this.postcode = payload.postcode || this.postcode;
			this.building_uid = payload.building_uid || this.building_uid;
			this.building_uid_appendix = payload.building_uid_appendix || '';
			this.street = payload.street || '';
			var child = this.$refs.locationAutocomplete;
			if (child && typeof child.changePhrase === 'function') {
				child.changePhrase(payload.location_name || '', payload.location_id || 0);
			}
			this.location = payload.location_id || null;
		},
		// Fills from a Google Address Validation result (App\Services\Address\DTO\
		// NormalizedAddress shape) - the international-address counterpart of
		// onPdokAddressResolved(). Never runs PDOK: this only ever fires for a non-NL,
		// Google-supported country.
		onInternationalAddressResolved(address) {
			this.addressLookupError = false;
			this.postcode = address.postal_code || '';
			this.building_uid = address.house_number || '';
			this.building_uid_appendix = address.unit_or_subpremise || '';
			this.street = address.street || '';
			this.administrative_area = address.administrative_area || '';
			this.address_line_2 = address.address_line_2 || '';
			this.address_source = address.provider || '';
			this.address_validation_status = address.validation_status || '';
			this.address_provider_reference = address.provider_reference || '';
			this.address_resolution_id = address.resolution_id || '';
			var child = this.$refs.locationAutocomplete;
			if (child && typeof child.changePhrase === 'function' && address.location_id) {
				child.changePhrase(address.locality || '', address.location_id);
			}
			this.location = address.location_id || null;
		},
		checkMail: function (event) {
			var email = event.target.value;
			if (email == '') return this.is_user_already_exists = 0;
			var vm = this;
			$.ajax({
				url: `/api/users/check-mail`,
				method: 'post',
				data: {
					email:email
				},
				success: function (result) {
					vm.is_user_already_exists = +result;
				},
				error: function () {
				}
			});
		},
		checkRecaptcha: function (result) {
			if (result === false) {
				this.recaptchaTicked = false;
			} else {
				this.recaptchaTicked = true;
			}
		},
		submitForm: function (e) {
			e.preventDefault();
			this.$validator.validateAll().then((res) => {
				if (this.isRecaptcha === 'true') {
					if (!this.recaptchaTicked) {
						sweetalert(window.app.locales.common.error, this.recaptchaMessage, 'error');
						return;
					}
				} 
				if (res) {
					let form = $('#form');
					let data = $('form').serialize();
					var message= this.successText;
					var errorMessage= this.errorText;
					if(this.showSuccess){
						$.ajax({
							url: form.attr('action'),
							method: 'post',
							data: data,
							success: function (result) {
								if(result.url!==undefined){
									if(message!==''){
										sweetalert(window.app.locales.common.success,message, 'success').then((res) => {
											window.location.replace(result.url);
										});
									}else{
										sweetalert(window.app.locales.common.success,window.app.locales.subscriptions.successfully_added, 'success').then((res) => {
											window.location.replace(result.url);
										});
									}
								}
							},
							error: function(result){
								if(result.responseJSON.url!==undefined){
									if(errorMessage!==''){
										sweetalert(window.app.locales.common.error,errorMessage, 'error').then((res) => {
											window.location.replace(result.responseJSON.url);
										});
									}else{
										sweetalert(window.app.locales.common.error,window.app.locales.common.error, 'error').then((res) => {
											window.location.replace(result.responseJSON.url);
										});
									}
								}
							}
						});
					}else{
						form.submit();
					}
				} else {
					sweetalert(window.app.locales.common.error, window.app.locales.subscriptions.form_with_errors, 'error');
				}
			});
		}
	}
}
</script>